import { useEffect, useMemo } from 'react';
import { io, Socket } from 'socket.io-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiClient } from './apiClient';
import { useApiClient } from './useApiClient';
import { env } from '@/utils/env';

const CHAT_BASE = '/chat';

export interface ChatMessage {
  id: string;
  senderId: string;
  message: string;
  timestamp: number;
  chatRoomId: string;
}

export interface ChatSummary {
  chatRoomId: string;
  otherUserId: string;
  createdAt: number;
  expiresAt: number | null;
  unreadCount: number;
  lastMessageAt: number;
}

export interface SendMessagePayload {
  senderId: string;
  recipientId: string;
  message: string;
}

const sendMessageRequest = async (client: ApiClient, payload: SendMessagePayload) =>
  client.post<{ success: boolean; message: ChatMessage; chatRoomId: string }>(
    `${CHAT_BASE}/send`,
    { body: payload },
  );

const getMessagesRequest = async (
  client: ApiClient,
  user1: string,
  user2: string,
  count = 50,
) =>
  client.get<{
    success: boolean;
    messages: ChatMessage[];
    chatRoomId: string;
    exists: boolean;
  }>(`${CHAT_BASE}/messages/${user1}/${user2}`, {
    searchParams: { count },
  });

const getUserChatsRequest = async (
  client: ApiClient,
  username: string,
  params: { limit?: number; offset?: number },
) =>
  client.get<{ success: boolean; chats: ChatSummary[]; pagination: { total: number } }>(
    `${CHAT_BASE}/${username}`,
    {
      searchParams: params,
    },
  );

const getStatusRequest = async (client: ApiClient, user1: string, user2: string) =>
  client.get<{
    success: boolean;
    exists: boolean;
    chatRoomId: string;
    messageCount: number;
  }>(`${CHAT_BASE}/status/${user1}/${user2}`);

export const useSendMessage = () => {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SendMessagePayload) => sendMessageRequest(client, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['chat', 'messages', variables.senderId, variables.recipientId],
      });
      queryClient.invalidateQueries({
        queryKey: ['chat', 'summary', variables.senderId],
      });
      queryClient.invalidateQueries({
        queryKey: ['chat', 'summary', variables.recipientId],
      });
    },
  });
};

export const useChatMessages = (
  user1: string,
  user2: string,
  options: { count?: number; enabled?: boolean } = {},
) => {
  const client = useApiClient();
  const { count = 50, enabled = true } = options;

  return useQuery({
    queryKey: ['chat', 'messages', user1, user2, count],
    queryFn: () => getMessagesRequest(client, user1, user2, count),
    enabled: enabled && Boolean(user1 && user2) && user1 !== user2,
  });
};

export const useChatStatus = (
  user1: string,
  user2: string,
  options: { enabled?: boolean } = {},
) => {
  const client = useApiClient();

  return useQuery({
    queryKey: ['chat', 'status', user1, user2],
    queryFn: () => getStatusRequest(client, user1, user2),
    enabled: options.enabled ?? Boolean(user1 && user2),
  });
};

export const useUserChats = (
  username: string,
  params: { limit?: number; offset?: number } = {},
) => {
  const client = useApiClient();

  return useQuery({
    queryKey: ['chat', 'summary', username, params],
    queryFn: () => getUserChatsRequest(client, username, params),
    enabled: Boolean(username),
  });
};

interface UseChatSocketOptions {
  userId: string | null;
  onMessage?: (message: ChatMessage) => void;
  onNotification?: (notification: {
    senderId: string;
    message: string;
    chatRoomId: string;
    timestamp: number;
  }) => void;
}

export const useChatSocket = ({
  userId,
  onMessage,
  onNotification,
}: UseChatSocketOptions) => {
  const socket = useMemo<Socket | null>(() => {
    if (!userId || typeof window === 'undefined') {
      return null;
    }
    const endpoint = env.apiBaseUrl.startsWith('http')
      ? env.apiBaseUrl.replace(/\/api$/, '')
      : window.location.origin;
    return io(endpoint.replace(/\/$/, ''), {
      path: `${CHAT_BASE}/socket.io`,
      transports: ['websocket'],
    });
  }, [userId]);

  useEffect(() => {
    if (!socket || !userId) {
      return undefined;
    }

    socket.emit('authenticate', userId);

    if (onMessage) {
      socket.on('newMessage', onMessage);
    }
    if (onNotification) {
      socket.on('messageNotification', onNotification);
    }

    return () => {
      if (onMessage) {
        socket.off('newMessage', onMessage);
      }
      if (onNotification) {
        socket.off('messageNotification', onNotification);
      }
      socket.disconnect();
    };
  }, [onMessage, onNotification, socket, userId]);

  return socket;
};
